import { useEffect, useRef, useState } from "react";
import { BRAND } from "@/constants/assets";

const PLATES = [
  {
    id: "hero-core",
    type: "logo",
    label: "ThemeStudios",
    sublabel: "Digital Designers",
    x: 14,
    y: 2,
    z: 1,
    w: 360,
    rot: -6,
    speed: 1,
    delay: 0,
  },
  {
    id: "identity",
    type: "copy",
    label: "Original layouts",
    sublabel: "Fresh creative systems",
    x: -26,
    y: -22,
    z: 0.66,
    w: 220,
    rot: -11,
    speed: 1.7,
    delay: 0.35,
  },
  {
    id: "briefs",
    type: "copy",
    label: "Copyright-safe",
    sublabel: "Brief-first process",
    x: 34,
    y: -26,
    z: 0.62,
    w: 220,
    rot: 9,
    speed: 1.5,
    delay: 0.55,
  },
  {
    id: "lanes",
    type: "copy",
    label: "6 service lanes",
    sublabel: "Posters to monthly systems",
    x: -20,
    y: 26,
    z: 0.74,
    w: 250,
    rot: 6,
    speed: 1.3,
    delay: 0.75,
  },
  {
    id: "whatsapp",
    type: "copy",
    label: "WhatsApp-first",
    sublabel: "Fast quote routing",
    x: 38,
    y: 26,
    z: 0.48,
    w: 196,
    rot: -9,
    speed: 2.2,
    delay: 1,
  },
];

export default function AboutHeroScene() {
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const plateRefs = useRef([]);
  const mouse = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const onMove = (event) => {
      const rect = el.getBoundingClientRect();
      mouse.current.tx = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.current.ty = ((event.clientY - rect.top) / rect.height) * 2 - 1;
    };

    el.addEventListener("mousemove", onMove);
    return () => el.removeEventListener("mousemove", onMove);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    let raf;
    let w = (canvas.width = canvas.offsetWidth * window.devicePixelRatio);
    let h = (canvas.height = canvas.offsetHeight * window.devicePixelRatio);

    const onResize = () => {
      w = canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      h = canvas.height = canvas.offsetHeight * window.devicePixelRatio;
    };

    window.addEventListener("resize", onResize);

    const particles = Array.from({ length: 64 }).map(() => ({
      x: Math.random() * w,
      y: Math.random() * h,
      r: Math.random() * 1.5 + 0.2,
      vx: (Math.random() - 0.5) * 0.16,
      vy: (Math.random() - 0.5) * 0.16,
      a: Math.random() * 0.35 + 0.06,
    }));

    setReady(true);
    const startTime = performance.now();

    const tick = () => {
      const t = (performance.now() - startTime) / 1000;

      mouse.current.x += (mouse.current.tx - mouse.current.x) * 0.06;
      mouse.current.y += (mouse.current.ty - mouse.current.y) * 0.06;

      PLATES.forEach((plate, index) => {
        const node = plateRefs.current[index];
        if (!node) return;
        const parallaxX = -mouse.current.x * 24 * plate.z;
        const parallaxY = -mouse.current.y * 24 * plate.z;
        const drift = Math.sin(t * plate.speed + plate.delay * 2.1) * 7;
        node.style.transform = `translate3d(${parallaxX}px, ${parallaxY + drift}px, 0) rotate(${plate.rot}deg) scale(1)`;
      });

      ctx.clearRect(0, 0, w, h);
      for (const particle of particles) {
        particle.x += particle.vx;
        particle.y += particle.vy;
        if (particle.x < 0) particle.x = w;
        if (particle.x > w) particle.x = 0;
        if (particle.y < 0) particle.y = h;
        if (particle.y > h) particle.y = 0;
        const px = particle.x + mouse.current.x * 8;
        const py = particle.y + mouse.current.y * 8;
        ctx.beginPath();
        ctx.arc(px, py, particle.r * window.devicePixelRatio, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${particle.a})`;
        ctx.fill();
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className="absolute inset-0 overflow-hidden"
      aria-hidden="true"
      style={{ opacity: ready ? 1 : 0, transition: "opacity 800ms ease" }}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_56%,rgba(224,60,49,0.22),transparent_28%),radial-gradient(circle_at_76%_72%,rgba(255,135,64,0.16),transparent_18%),radial-gradient(circle_at_22%_28%,rgba(255,255,255,0.05),transparent_24%)]" />
      <div className="about-hero-bloom about-hero-bloom--main" />
      <div className="about-hero-bloom about-hero-bloom--rim" />

      <div className="about-hero-wordmark about-hero-wordmark--theme">THEME</div>
      <div className="about-hero-wordmark about-hero-wordmark--studios">STUDIOS</div>

      <div className="about-hero-ring about-hero-ring--one" />
      <div className="about-hero-ring about-hero-ring--two" />

      <div className="about-hero-stage">
        {PLATES.map((plate, index) => (
          <div
            key={plate.id}
            ref={(node) => (plateRefs.current[index] = node)}
            className={`about-hero-plate about-hero-plate--${plate.type}`}
            style={{
              left: `calc(50% + ${plate.x}%)`,
              top: `calc(50% + ${plate.y}%)`,
              width: `${plate.w}px`,
              zIndex: Math.round(plate.z * 10),
              opacity: Math.max(0.54, plate.z),
              filter: `blur(${(1 - plate.z) * 2.2}px) brightness(${0.6 + plate.z * 0.42})`,
              animation: `about-plate-in 1100ms cubic-bezier(0.22, 1, 0.36, 1) ${plate.delay}s both`,
            }}
          >
            {plate.type === "logo" ? (
              <div className="about-hero-logo-shell">
                <div className="about-hero-logo-core">
                  <img
                    src={BRAND.logoDark}
                    alt=""
                    draggable={false}
                    loading="eager"
                    className="about-hero-logo-image"
                  />
                </div>
                <div className="about-hero-logo-copy">
                  <p>{plate.label}</p>
                  <span>{plate.sublabel}</span>
                </div>
              </div>
            ) : (
              <div className="about-hero-copy-shell">
                <div className="about-hero-copy-top">
                  <span className="about-hero-copy-dot" />
                  <span className="about-hero-copy-line about-hero-copy-line--short" />
                </div>
                <div className="about-hero-copy-bars">
                  <span className="about-hero-copy-bar about-hero-copy-bar--high" />
                  <span className="about-hero-copy-bar about-hero-copy-bar--mid" />
                  <span className="about-hero-copy-bar about-hero-copy-bar--low" />
                </div>
                <div className="about-hero-copy-bottom">
                  <p>{plate.label}</p>
                  <span>{plate.sublabel}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <canvas
        ref={canvasRef}
        className="absolute inset-0 h-full w-full"
        style={{ width: "100%", height: "100%" }}
      />

      <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/72 to-[#050505]/26 pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#050505] to-transparent pointer-events-none" />

      <style>{`
        .about-hero-stage {
          position: absolute;
          inset: 0;
          perspective: 1400px;
          transform-style: preserve-3d;
        }
        .about-hero-wordmark {
          position: absolute;
          right: 6%;
          font-family: "Cabinet Grotesk", sans-serif;
          font-size: clamp(5.5rem, 13vw, 10rem);
          font-weight: 900;
          letter-spacing: -0.06em;
          line-height: 0.82;
          color: rgba(255,255,255,0.04);
          text-shadow: 0 0 40px rgba(255,255,255,0.02);
          pointer-events: none;
          user-select: none;
        }
        .about-hero-wordmark--theme {
          top: 18%;
        }
        .about-hero-wordmark--studios {
          top: 31%;
        }
        .about-hero-ring {
          position: absolute;
          border-radius: 999px;
          border: 1px solid rgba(255,255,255,0.08);
          pointer-events: none;
        }
        .about-hero-ring--one {
          right: 12%;
          top: 14%;
          width: min(44vw, 560px);
          height: min(44vw, 560px);
          opacity: 0.42;
        }
        .about-hero-ring--two {
          right: 20%;
          top: 22%;
          width: min(31vw, 390px);
          height: min(31vw, 390px);
          opacity: 0.18;
        }
        .about-hero-bloom {
          position: absolute;
          border-radius: 999px;
          filter: blur(54px);
          pointer-events: none;
        }
        .about-hero-bloom--main {
          right: 9%;
          top: 24%;
          width: 520px;
          height: 520px;
          background: radial-gradient(circle, rgba(224,60,49,0.38) 0%, rgba(224,60,49,0.08) 38%, transparent 72%);
          animation: about-bloom-pulse 7s ease-in-out infinite;
        }
        .about-hero-bloom--rim {
          right: 21%;
          bottom: 8%;
          width: 360px;
          height: 360px;
          background: radial-gradient(circle, rgba(16,185,129,0.15) 0%, transparent 72%);
          animation: about-bloom-pulse 9s ease-in-out infinite reverse;
        }
        .about-hero-plate {
          position: absolute;
          transform-origin: center center;
          translate: -50% -50%;
          will-change: transform, opacity;
          border-radius: 1.6rem;
          border: 1px solid rgba(255,255,255,0.08);
          background:
            linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02)),
            rgba(8,10,14,0.62);
          box-shadow:
            0 30px 80px -42px rgba(0,0,0,0.92),
            inset 0 1px 0 rgba(255,255,255,0.08);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          overflow: hidden;
        }
        .about-hero-plate--logo {
          padding: 1rem;
        }
        .about-hero-plate--copy {
          padding: 0.95rem;
        }
        .about-hero-logo-shell {
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 0.9rem;
          padding: 0.65rem;
        }
        .about-hero-logo-core {
          position: relative;
          display: grid;
          place-items: center;
          aspect-ratio: 1;
          border-radius: 1.35rem;
          border: 1px solid rgba(255,255,255,0.08);
          background:
            radial-gradient(circle at 24% 22%, rgba(255,255,255,0.16), transparent 26%),
            linear-gradient(145deg, rgba(4,10,14,0.98), rgba(8,15,22,0.84));
          overflow: hidden;
        }
        .about-hero-logo-core::before {
          content: "";
          position: absolute;
          inset: 8%;
          border-radius: 1.2rem;
          border: 1px solid rgba(255,255,255,0.08);
          background: radial-gradient(circle, rgba(255,255,255,0.06), transparent 74%);
        }
        .about-hero-logo-core::after {
          content: "";
          position: absolute;
          width: 74%;
          height: 74%;
          border-radius: 999px;
          background: radial-gradient(circle, rgba(224,60,49,0.22), transparent 68%);
          filter: blur(24px);
        }
        .about-hero-logo-image {
          position: relative;
          z-index: 1;
          width: 78%;
          height: auto;
          object-fit: contain;
          border-radius: 1.1rem;
          box-shadow:
            0 26px 48px -30px rgba(0,0,0,0.88),
            0 0 0 1px rgba(255,255,255,0.04);
        }
        .about-hero-logo-copy {
          display: flex;
          align-items: end;
          justify-content: space-between;
          gap: 1rem;
        }
        .about-hero-logo-copy p,
        .about-hero-copy-bottom p {
          margin: 0;
          font-family: "Cabinet Grotesk", sans-serif;
          font-size: 0.92rem;
          font-weight: 800;
          letter-spacing: -0.02em;
          color: #f6f7fb;
        }
        .about-hero-logo-copy span,
        .about-hero-copy-bottom span {
          display: block;
          color: rgba(255,255,255,0.58);
          font-size: 0.62rem;
          font-weight: 700;
          letter-spacing: 0.16em;
          text-transform: uppercase;
        }
        .about-hero-copy-shell {
          display: flex;
          flex-direction: column;
          gap: 0.7rem;
          min-height: 100%;
        }
        .about-hero-copy-top {
          display: flex;
          align-items: center;
          gap: 0.38rem;
        }
        .about-hero-copy-dot {
          width: 0.42rem;
          height: 0.42rem;
          border-radius: 999px;
          background: #e03c31;
          box-shadow: 0 0 12px rgba(224,60,49,0.45);
        }
        .about-hero-copy-line {
          display: block;
          height: 0.28rem;
          border-radius: 999px;
          background: rgba(255,255,255,0.14);
        }
        .about-hero-copy-line--short {
          width: 32%;
        }
        .about-hero-copy-bars {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 0.34rem;
          align-items: end;
          height: 3rem;
        }
        .about-hero-copy-bar {
          border-radius: 0.65rem 0.65rem 0.18rem 0.18rem;
          background: linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04));
        }
        .about-hero-copy-bar--high {
          height: 100%;
        }
        .about-hero-copy-bar--mid {
          height: 66%;
        }
        .about-hero-copy-bar--low {
          height: 44%;
        }
        .about-hero-copy-bottom {
          margin-top: auto;
        }
        @keyframes about-bloom-pulse {
          0%, 100% { transform: scale(1); opacity: 0.82; }
          50% { transform: scale(1.08); opacity: 1; }
        }
        @keyframes about-plate-in {
          0% {
            opacity: 0;
            transform: translate3d(0, 34px, 0) rotate(0deg) scale(0.92);
            filter: blur(8px);
          }
          100% {
            opacity: var(--plate-opacity, 0.9);
            filter: blur(var(--plate-blur, 0px));
          }
        }
        @media (max-width: 1100px) {
          .about-hero-wordmark {
            right: -2%;
            font-size: clamp(4rem, 12vw, 7rem);
          }
          .about-hero-ring--one {
            width: min(52vw, 460px);
            height: min(52vw, 460px);
          }
          .about-hero-ring--two {
            width: min(36vw, 320px);
            height: min(36vw, 320px);
          }
        }
        @media (max-width: 768px) {
          .about-hero-wordmark {
            right: auto;
            left: 50%;
            transform: translateX(-50%);
            font-size: clamp(3rem, 18vw, 5.8rem);
            text-align: center;
            width: 100%;
          }
          .about-hero-wordmark--theme {
            top: 12%;
          }
          .about-hero-wordmark--studios {
            top: 21%;
          }
          .about-hero-ring--one {
            left: 50%;
            right: auto;
            top: 14%;
            transform: translateX(-50%);
            width: min(92vw, 380px);
            height: min(92vw, 380px);
          }
          .about-hero-ring--two {
            left: 50%;
            right: auto;
            top: 24%;
            transform: translateX(-50%);
            width: min(68vw, 280px);
            height: min(68vw, 280px);
          }
          .about-hero-bloom--main {
            left: 50%;
            right: auto;
            top: 20%;
            transform: translateX(-50%);
            width: 360px;
            height: 360px;
          }
          .about-hero-bloom--rim {
            left: 58%;
            right: auto;
            bottom: 16%;
            width: 220px;
            height: 220px;
          }
          .about-hero-plate {
            transform: scale(0.82);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .about-hero-bloom {
            animation: none;
          }
          .about-hero-plate {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}
